/* eslint-disable react/jsx-props-no-spreading */
import { FunctionalComponent, RefObject, createRef, h } from 'preact';
import { Ref, useLayoutEffect, useState } from 'preact/hooks';
import VirtualList, { ScrollDirection, Props as VirtualListProps } from './TinyVirtualList';
import { forwardRef, memo } from 'preact/compat';
import { RefContainer } from './VirtualList.style';
import { useDeviceState } from '../../hooks';

interface Props extends Omit<VirtualListProps, 'height'> {
  isVisible: boolean;
  overscanCount?: number;
  scrollDirection?: ScrollDirection;
  scrollToIndex?: number;
  ref?: RefObject<HTMLDivElement>;
  stickyRef: Ref<HTMLDivElement> | undefined;
}
const VirtualListWrapper: FunctionalComponent<Props> = forwardRef<HTMLDivElement, Props>(
  (
    {
      estimatedItemSize = 50,
      isVisible,
      overscanCount = 50,
      scrollDirection = ScrollDirection.VERTICAL,
      scrollToIndex,
      stickyRef,
      ...props
    },
    scrollerRef,
  ) => {
    const maxParentHeight = 300;
    const minParentHeight = 50;
    const [parentHeight, setHeight] = useState(maxParentHeight);
    const { isDesktop } = useDeviceState();
    const virtualListRef = createRef();

    useLayoutEffect(() => {
      if (isVisible && virtualListRef.current) {
        const { offsetHeight } = virtualListRef.current;

        if (offsetHeight < minParentHeight) {
          return; // no need to update
        }

        if (offsetHeight !== parentHeight) {
          setHeight(offsetHeight);
        }
      }
    }, [isVisible, parentHeight, virtualListRef]);

    return (
      <RefContainer ref={virtualListRef}>
        {isVisible && (
          <VirtualList
            scrollerRef={scrollerRef}
            stickyRef={stickyRef}
            virtualListRef={virtualListRef}
            estimatedItemSize={estimatedItemSize}
            height={parentHeight}
            overscanCount={overscanCount}
            scrollDirection={scrollDirection}
            scrollToIndex={scrollToIndex}
            isDesktop={isDesktop}
            {...props}
          />
        )}
      </RefContainer>
    );
  },
);

export default memo(VirtualListWrapper);
